import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useForm, Controller } from "react-hook-form";
import CustomSelect from "../../../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../../FormFields/CustomInput/CustomInput";
import { Bed as BedIcon } from "@mui/icons-material";
import { useMutation, useQueryClient, useQuery } from "react-query";
import {
  createBedRailAssessment,
  getBedRailQuestions,
} from "../../../../../api/services/Assessment/BedRailAssessment";

const BedRailAssessmentModal = ({ open, handleClose, clientId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset, formState } = useForm();
  const { dirtyFields } = formState;

  // fetch questions when modal opens
  const { data: questions = [] } = useQuery(
    ["bedRailQuestions"],
    getBedRailQuestions,
    { enabled: open }
  );

  const mutation = useMutation(
    async (answers) => {
      const touchedKeys = Object.keys(dirtyFields);
      for (const key of touchedKeys) {
        const payload = {
          client: clientId,
          question: parseInt(key, 10),
          value: answers[key]?.choice || null,
          comment: answers[key]?.comment || "",
        };
        await createBedRailAssessment(payload);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["bedRailAssessments", clientId]);
        enqueueSnackbar("Bed Rail assessment created", { variant: "success" });
        reset();
        handleClose();
      },
      onError: () => {
        enqueueSnackbar("Failed to create assessment", { variant: "error" });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onClose={() => { reset(); handleClose(); }} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BedIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bed Rail Assessment
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            {questions.map((q) => (
              <Grid item xs={12} key={q.id}>
                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {q.label}
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }} direction={{ xs: "column", sm: "row" }}>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`${q.id}.choice`}
                        control={control}
                        rules={{ required: "Required" }}
                        render={({ field }) => (
                          <CustomSelect
                            name={`${q.id}.choice`}
                            label="Select"
                            options={[
                              { value: "yes", label: "Yes" },
                              { value: "no", label: "No" },
                            ]}
                            selectedValue={field.value}
                            onChange={field.onChange}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <Controller
                        name={`${q.id}.comment`}
                        control={control}
                        render={({ field }) => (
                          <CustomInput
                            name={`${q.id}.comment`}
                            label="Comment"
                            value={field.value}
                            onChange={field.onChange}
                            multiline
                            rows={3}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => { reset(); handleClose(); }}>Cancel</Button>
        <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BedRailAssessmentModal;